import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { Dropdown } from '../components/ui/Dropdown.js';
import { ListState } from '../components/ui/ListState.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { Recorrido } from '../components/ui/Recorrido.js';
import { useRecorrido } from '../hooks/useRecorrido.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { PillGroup, Pill } from '../components/PillGroup.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { useInfiniteContacts, useCategories, useBulkCategorize, useBulkDelete, useContactTagsList, useBulkTagContacts, useCreateContactTag, useCreateContactCategory, type Contact } from '../hooks/useContacts.js';
import { Modal } from '../components/ui/Modal.js';
import { Checkbox } from '../components/ui/Checkbox.js';
import { relTime } from '../lib/relTime.js';
import React from 'react';
import { ContactImportWizard } from '../components/contacts/ContactImportWizard.js';
import { CreateContactForm } from '../components/contacts/CreateContactForm.js';
import { TimeBandedList } from '../components/common/TimeBandedList.js';
import { getContactCategoryTone as getCategoryTone } from '../lib/contactCategoryTone.js';

// ── Tabla V2 ──────────────────────────────────────────────────────────────────
// Misma rejilla para cabecera y filas (spec del handoff): la columna del
// cotejo mide --evo-table-check-col para que el ritmo coincida con las
// listas de tarjeta.
const GRID_COLS = 'var(--evo-table-check-col) minmax(0, 2fr) minmax(0, 1.6fr) minmax(0, 1.1fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1fr)';

const HEAD_CELL: React.CSSProperties = {
  padding: 'var(--evo-table-head-py) var(--evo-table-cell-px)',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--evo-action-pill-gap)',
  color: 'var(--evo-fg-3)',
  fontFamily: 'var(--evo-font)',
  fontSize: 'var(--evo-list-header-fs)',
  fontWeight: 'var(--evo-list-header-fw)' as unknown as number,
  textTransform: 'uppercase',
  letterSpacing: 'var(--evo-ls-caps)',
  whiteSpace: 'nowrap',
};

const DATA_CELL: React.CSSProperties = {
  padding: 'var(--evo-table-cell-py) var(--evo-table-cell-px)',
  fontFamily: 'var(--evo-font)',
  fontSize: 'var(--evo-list-subtitle-fs)',
  color: 'var(--evo-fg-3)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// ── Main page ─────────────────────────────────────────────────────────────────

export function ContactsPage() {
  const { t } = useTranslation();
  const { setOverride } = useTopBarCtx();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState('');
  const [source, setSource] = useState('');
  const [tag, setTag] = useState('');
  const [sortBy, setSortBy] = useState<'updated' | 'messaging'>('updated');
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [showImport, setShowImport] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [collapsedBands, setCollapsedBands] = useState<Set<string>>(new Set());
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();

  // ?selected=id (búsqueda global) ahora navega al detalle a pantalla completa
  useEffect(() => {
    const sel = searchParams.get('selected');
    if (sel) {
      const parsed = parseInt(sel);
      if (!isNaN(parsed)) navigate(`/contacts/${parsed}`, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('selected')]);
  // SAB add-tag state
  const [tagCreateInput, setTagCreateInput] = useState('');
  const [tagCreateMode, setTagCreateMode] = useState(false);
  // SAB add-category state
  const [categoryCreateInput, setCategoryCreateInput] = useState('');
  const [categoryCreateMode, setCategoryCreateMode] = useState(false);
  useEffect(() => {
    setOverride({
      title: t('contacts.pageTitle'),
      heroIcon: 'contacts',
      heroIconColor: 'var(--evo-cyan)',
      bottomActions: (
        // El div es el ancla del recorrido guiado y nada más: `display: flex`
        // no cambia el dibujo (PillGroup ya es flex), y así no hay que sumarle
        // una prop al primitive Pill, que tiene 196 usos.
        <div data-tour-id="contacts-nuevo" style={{ display: 'flex' }}>
          <PillGroup size="md">
            <Pill tone="cyan" icon="plus" onClick={() => setShowCreate(true)}>
              {t('contacts.create.title')}
            </Pill>
            <Pill tone="cyan" icon="contacts" onClick={() => setShowImport(true)}>
              {t('contacts.importCsv')}
            </Pill>
          </PillGroup>
        </div>
      ),
    });
    return () => setOverride({});
  }, [setOverride, t]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: infiniteData, isLoading, isError, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteContacts({ search: debouncedSearch, category, source, tag, sortBy });
  const { data: categories } = useCategories();
  const { data: allTags = [] } = useContactTagsList();
  const bulkCategorize = useBulkCategorize();
  const bulkDelete = useBulkDelete();
  const bulkTag = useBulkTagContacts();
  const createTag = useCreateContactTag();
  const createCategory = useCreateContactCategory();

  const contacts: Contact[] = useMemo(
    () => infiniteData?.pages.flatMap(p => p.rows) ?? [],
    [infiniteData],
  );
  const totalContacts = infiniteData?.pages[0]?.total ?? 0;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);


  const toggleCheck = useCallback((id: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setChecked(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }, []);

  const handleBulkDelete = async () => {
    if (checked.size === 0) return;
    await bulkDelete.mutateAsync(Array.from(checked));
    setChecked(new Set());
  };

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && checked.size > 0) setChecked(new Set());
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [checked.size]);

  // El recorrido decide solo si sale: primera visita, uno por sesión, y
  // `listo` para no medir un esqueleto de carga.
  const recorrido = useRecorrido('contacts', { hayDatos: contacts.length > 0, listo: !isLoading });

  return (
    <div className="flex flex-col h-full animate-fade-blur-in" style={{ background: 'var(--evo-bg)' }}>
      <Recorrido
        pasos={recorrido.pasos}
        abierto={recorrido.abierto}
        onCerrar={recorrido.cerrar}
        onTerminar={recorrido.terminar}
      />

      <FilterRow tourId="contacts-toolbar">
        <FilterRow.Search
          value={search}
          onChange={v => setSearch(v)}
          onSubmit={() => setDebouncedSearch(search)}
          onClear={() => { setSearch(''); setDebouncedSearch(''); }}
          placeholder={t('common.search')}
        />
        <FilterRow.Dropdown<string>
          value={category}
          placeholder={t('contacts.allCategories')}
          options={[
            { value: '', label: t('contacts.allCategories') },
            ...(categories?.map(c => ({ value: c.category, label: t('categories.' + c.category, { defaultValue: c.category }) + ' (' + c.count + ')' })) ?? []),
          ]}
          onChange={val => setCategory(val)}
        />
        <FilterRow.Dropdown<string>
          value={source}
          placeholder={t('contacts.allSources', { defaultValue: 'All sources' })}
          options={[
            { value: '', label: t('contacts.allSources', { defaultValue: 'All sources' }) },
            { value: 'email', label: t('contacts.source_email', { defaultValue: 'Email' }) },
            { value: 'lead-engine', label: t('contacts.source_lead-engine', { defaultValue: 'Lead engine' }) },
            { value: 'manual', label: t('contacts.source_manual', { defaultValue: 'Manual' }) },
            { value: 'csv-import', label: t('contacts.source_csv-import', { defaultValue: 'CSV import' }) },
          ]}
          onChange={val => setSource(val)}
        />
        <FilterRow.Dropdown<string>
          value={tag}
          placeholder={t('contacts.allTags', { defaultValue: 'All tags' })}
          options={[
            { value: '', label: t('contacts.allTags', { defaultValue: 'All tags' }) },
            ...allTags.map(tg => ({ value: tg.slug, label: tg.label })),
          ]}
          onChange={val => setTag(val)}
        />
        <FilterRow.Dropdown<'updated' | 'messaging'>
          value={sortBy}
          placeholder={t('contacts.sortBy.updated', { defaultValue: 'Recently updated' })}
          options={[
            { value: 'updated', label: t('contacts.sortBy.updated', { defaultValue: 'Recently updated' }) },
            { value: 'messaging', label: t('contacts.sortBy.messaging', { defaultValue: 'Recent messaging activity' }) },
          ]}
          onChange={val => setSortBy(val)}
        />
      </FilterRow>

      <SelectionActionBar
        visible={checked.size > 0}
        countLabel={t('contacts.selected', { count: checked.size })}
        onClearSelection={() => { setChecked(new Set()); setTagCreateMode(false); setTagCreateInput(''); setCategoryCreateMode(false); setCategoryCreateInput(''); }}
      >
        {categoryCreateMode ? (
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <div style={{ width: 140 }}>
              <Input
                autoFocus
                type="text"
                size="sm"
                fullWidth
                value={categoryCreateInput}
                onChange={v => setCategoryCreateInput(v)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && categoryCreateInput.trim()) {
                    const name = categoryCreateInput.trim();
                    createCategory.mutate(name, {
                      onSuccess: newCat => {
                        bulkCategorize.mutate({ ids: Array.from(checked), category: newCat.name }, {
                          onSuccess: () => setChecked(new Set()),
                        });
                        setCategoryCreateMode(false);
                        setCategoryCreateInput('');
                      },
                    });
                  }
                  if (e.key === 'Escape') { setCategoryCreateMode(false); setCategoryCreateInput(''); }
                }}
                placeholder={t('contacts.category.newPlaceholder', { defaultValue: 'New category name…' })}
              />
            </div>
            <Button
              variant="solid"
              tone="cyan"
              size="xs"
              disabled={!categoryCreateInput.trim()}
              onClick={() => {
                if (!categoryCreateInput.trim()) return;
                const name = categoryCreateInput.trim();
                createCategory.mutate(name, {
                  onSuccess: newCat => {
                    bulkCategorize.mutate({ ids: Array.from(checked), category: newCat.name }, {
                      onSuccess: () => setChecked(new Set()),
                    });
                    setCategoryCreateMode(false);
                    setCategoryCreateInput('');
                  },
                });
              }}
            >
              {t('common.create', { defaultValue: 'Create' })}
            </Button>
          </div>
        ) : (
          <Dropdown<string>
            value={null}
            placeholder={t('contacts.moveToCategory')}
            options={[
              ...(categories?.map(c => ({ value: c.category, label: t('categories.' + c.category, { defaultValue: c.category }) })) ?? []),
              { value: '__create__', label: `+ ${t('contacts.category.new', { defaultValue: 'New category' })}` },
            ]}
            onChange={val => {
              if (!val || checked.size === 0) return;
              if (val === '__create__') { setCategoryCreateMode(true); return; }
              bulkCategorize.mutate({ ids: Array.from(checked), category: val }, { onSuccess: () => setChecked(new Set()) });
            }}
            size="sm"
          />
        )}
        <SelectionActionBar.Divider />
        {/* Add tag */}
        {tagCreateMode ? (
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <div style={{ width: 140 }}>
              <Input
                autoFocus
                type="text"
                size="sm"
                fullWidth
                value={tagCreateInput}
                onChange={v => setTagCreateInput(v)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && tagCreateInput.trim()) {
                    const slug = tagCreateInput.trim().toLowerCase().replace(/\s+/g, '_');
                    createTag.mutate({ slug, label: tagCreateInput.trim() }, {
                      onSuccess: newTag => {
                        bulkTag.mutate({ ids: Array.from(checked), tagId: newTag.id, action: 'add' });
                        setTagCreateMode(false);
                        setTagCreateInput('');
                      },
                    });
                  }
                  if (e.key === 'Escape') { setTagCreateMode(false); setTagCreateInput(''); }
                }}
                placeholder={t('contacts.tag.newPlaceholder', { defaultValue: 'New tag name…' })}
              />
            </div>
            <Button
              variant="solid"
              tone="cyan"
              size="xs"
              disabled={!tagCreateInput.trim()}
              onClick={() => {
                if (!tagCreateInput.trim()) return;
                const slug = tagCreateInput.trim().toLowerCase().replace(/\s+/g, '_');
                createTag.mutate({ slug, label: tagCreateInput.trim() }, {
                  onSuccess: newTag => {
                    bulkTag.mutate({ ids: Array.from(checked), tagId: newTag.id, action: 'add' });
                    setTagCreateMode(false);
                    setTagCreateInput('');
                  },
                });
              }}
            >
              {t('common.create', { defaultValue: 'Create' })}
            </Button>
          </div>
        ) : (
          <Dropdown<string>
            value={null}
            placeholder={t('contacts.addTag', { defaultValue: 'Add tag' })}
            options={[
              ...allTags.map(tg => ({ value: String(tg.id), label: tg.label })),
              { value: '__create__', label: `+ ${t('contacts.tag.new', { defaultValue: 'New tag' })}` },
            ]}
            onChange={val => {
              if (!val || checked.size === 0) return;
              if (val === '__create__') { setTagCreateMode(true); return; }
              bulkTag.mutate({ ids: Array.from(checked), tagId: parseInt(val), action: 'add' });
            }}
            size="sm"
          />
        )}
        <SelectionActionBar.Divider />
        <SelectionActionBar.Action
          label={t('common.delete')}
          tone="danger"
          loading={bulkDelete.isPending}
          onClick={() => void handleBulkDelete()}
          confirmRequired
        />
      </SelectionActionBar>

      {/* Lista tabular V2 — navega a /contacts/:id (sin fila seleccionada:
          la selección de panel murió con el split-pane) */}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">

          {/* Cabecera de tabla */}
          <div style={{ display: 'grid', gridTemplateColumns: GRID_COLS, height: 'var(--evo-toolbar-height)', alignItems: 'center', borderBottom: '1px solid var(--evo-stroke)', flexShrink: 0, background: 'var(--evo-bg-2)' }}>
            <div data-tour-id="contacts-seleccion" style={{ padding: 'var(--evo-table-head-py) var(--evo-list-checkbox-gap) var(--evo-table-head-py) var(--evo-list-pad-x)' }}>
              <Checkbox
                checked={contacts.length > 0 && contacts.every(c => checked.has(c.id))}
                indeterminate={checked.size > 0 && !contacts.every(c => checked.has(c.id))}
                onChange={sel => {
                  if (sel) setChecked(new Set(contacts.map(c => c.id)));
                  else setChecked(new Set());
                }}
              />
            </div>
            <div style={{ ...HEAD_CELL, padding: 'var(--evo-table-head-py) var(--evo-table-cell-px) var(--evo-table-head-py) 0' }}>{t('contacts.columns.contact', { defaultValue: 'Contacto' })}</div>
            <div style={HEAD_CELL}>{t('contacts.columns.email', { defaultValue: 'Email' })}</div>
            <div style={HEAD_CELL}>{t('contacts.columns.phone', { defaultValue: 'Teléfono' })}</div>
            <div data-tour-id="contacts-col-origen" style={HEAD_CELL}>{t('contacts.columns.source', { defaultValue: 'Origen' })}</div>
            <div data-tour-id="contacts-col-categoria" style={HEAD_CELL}>{t('contacts.columns.category', { defaultValue: 'Categoría' })}</div>
            <div style={{ ...HEAD_CELL, padding: 'var(--evo-table-head-py) var(--evo-list-pad-x) var(--evo-table-head-py) var(--evo-table-cell-px)', justifyContent: 'flex-end' }}>{t('contacts.columns.updated', { defaultValue: 'Actualizado' })}</div>
          </div>

          {/* Filas con bandas de tiempo */}
          <div className="flex-1 overflow-y-auto" data-tour-id="contacts-lista">
            {isLoading && <LoadingState size="sm" />}
            {!isLoading && contacts.length === 0 && (
              <ListState
                isLoading={false} isError={isError} error={error}
                isEmpty emptyTitle={t('contacts.noContacts')}
                onRetry={() => { void refetch(); }}
              >{null}</ListState>
            )}
            {contacts.length > 0 && (
              <TimeBandedList<Contact>
                items={contacts}
                getTimestamp={(c) => c.updatedAt}
                getId={(c) => c.id}
                checkedIds={checked}
                onBandCheck={(ids, allChecked) => {
                  setChecked(prev => {
                    const next = new Set(prev);
                    if (allChecked) { ids.forEach(id => next.delete(id)); }
                    else { ids.forEach(id => next.add(id)); }
                    return next;
                  });
                }}
                collapsedBands={collapsedBands}
                onToggleBand={(band) => setCollapsedBands(prev => {
                  const next = new Set(prev);
                  next.has(band) ? next.delete(band) : next.add(band);
                  return next;
                })}
                renderItem={(contact) => {
                  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.businessName || '—';
                  const sub = contact.businessName && name !== contact.businessName ? contact.businessName : null;
                  return (
                    // Solo la PRIMERA fila lleva el ancla: el recorrido resalta
                    // una ficha de ejemplo, no las doscientas.
                    <div
                      key={contact.id}
                      data-tour-id={contact.id === contacts[0]?.id ? 'contacts-fila' : undefined}
                      style={{ display: 'flex', flexDirection: 'column' }}
                    >
                      <div
                        data-list-item-id={contact.id}
                        onClick={() => navigate(`/contacts/${contact.id}`)}
                        style={{ display: 'grid', gridTemplateColumns: GRID_COLS, alignItems: 'stretch', minHeight: 'var(--evo-list-row-min-height)', cursor: 'pointer' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--evo-list-row-hover-bg)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = ''; }}
                      >
                        <div
                          style={{ display: 'flex', alignItems: 'flex-start', padding: 'calc(var(--evo-table-cell-py) + var(--evo-list-check-nudge)) var(--evo-list-checkbox-gap) var(--evo-table-cell-py) var(--evo-list-pad-x)' }}
                          onClick={e => e.stopPropagation()}
                        >
                          <Checkbox checked={checked.has(contact.id)} onChange={() => toggleCheck(contact.id)} />
                        </div>
                        <div style={{ padding: 'var(--evo-table-cell-py) var(--evo-table-cell-px) var(--evo-table-cell-py) 0', overflow: 'hidden' }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--evo-fg-1)', fontSize: 'var(--evo-list-title-fs)', fontWeight: 400 }}>{name}</div>
                          {sub && <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2, color: 'var(--evo-fg-3)', fontSize: 'var(--evo-list-subtitle-fs)' }}>{sub}</div>}
                        </div>
                        <div style={DATA_CELL}>{contact.primaryEmail ?? '—'}</div>
                        <div style={DATA_CELL}>{contact.primaryPhone ?? '—'}</div>
                        <div style={{ padding: 'var(--evo-table-cell-py) var(--evo-table-cell-px)', display: 'flex', alignItems: 'flex-start' }}>
                          {contact.source ? <Pill size="xs" tone="muted">{t(`contacts.source_${contact.source}`, { defaultValue: contact.source })}</Pill> : <span style={{ color: 'var(--evo-fg-4)', fontSize: 'var(--evo-list-subtitle-fs)' }}>—</span>}
                        </div>
                        <div style={{ padding: 'var(--evo-table-cell-py) var(--evo-table-cell-px)', display: 'flex', alignItems: 'flex-start', gap: 4, flexWrap: 'wrap' }}>
                          {contact.category ? <Pill size="xs" tone={getCategoryTone(contact.category)}>{t(`contacts.category.${contact.category}`, { defaultValue: contact.category })}</Pill> : <span style={{ color: 'var(--evo-fg-4)', fontSize: 'var(--evo-list-subtitle-fs)' }}>—</span>}
                        </div>
                        <div style={{ ...DATA_CELL, padding: 'var(--evo-table-cell-py) var(--evo-list-pad-x) var(--evo-table-cell-py) var(--evo-table-cell-px)', textAlign: 'right' as const }}>{relTime(contact.updatedAt, t as (k: string, opts?: Record<string, unknown>) => string)}</div>
                      </div>
                      <div style={{ margin: '0 var(--evo-list-pad-x)', height: 1, background: 'var(--evo-stroke)' }} />
                    </div>
                  );
                }}
                loadMoreSentinel={
                  <div ref={sentinelRef} style={{ height: 1 }}>
                    {isFetchingNextPage && <LoadingState size="sm" />}
                  </div>
                }
              />
            )}
          </div>
        </div>
      </div>

      {/* New contact */}
      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title={t('contacts.create.title')}
        size="sm"
      >
        <CreateContactForm
          onSuccess={c => { setShowCreate(false); navigate(`/contacts/${c.id}`); }}
          onCancel={() => setShowCreate(false)}
        />
      </Modal>

      {/* Import wizard */}
      {showImport && <ContactImportWizard onClose={() => setShowImport(false)} />}
    </div>
  );
}
